import { useEffect, useMemo } from 'react';
import {
  isRouteErrorResponse,
  type LinkDescriptor,
  Links,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from 'react-router';

import { apiIsomorphicFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import { App } from '#/app';
import { AppConfigContextProvider } from '#/config/AppConfigContext';
import { UserEnvironmentContextProvider } from '#/contexts/UserEnvironmentContext';
import { I18nContext } from '#/i18n/contexts/I18nContext';
import { createI18nFn } from '#/i18n/createI18nFn';
import { useIsInEmbedSearchWidget } from '#/modules/embedSearchWidget/hooks/useIsInEmbedSearchWidget';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import type { HorizonLoaderFunctionArgs } from '#/routing/types/HorizonLoaderFunctionArgs';
import type { HorizonLoaderFunctionData } from '#/routing/types/HorizonLoaderFunctionData';
import themeGlobalCss from '#/theme/theme-global.css?raw';
import { HorizonFlashStoreProvider } from '#/zustand-stores/horizonFlashStore';

import { HorizonBrowserNotSupportedScript } from './scripts/HorizonBrowserNotSupportedScript';
import { HorizonGoogleMapsScripts } from './scripts/HorizonGoogleMapsScripts';
import { HorizonSetIdealistObjectScript } from './scripts/HorizonSetIdealistObjectScript';
import { HorizonTrackJsScript } from './scripts/HorizonTrackJsScript';
import { useMatchesOverrideLocale } from './useMatchesOverrideLocale';
import { deleteCookie } from '#/utils/cookies';
import { FLASHES_COOKIE } from '#/zustand-stores/flashesStore';
import { HorizonMixpanelScript } from '#/horizon/scripts/HorizonMixpanelScript';
import { HorizonGoogleTagManagerScript } from '#/horizon/scripts/HorizonGoogleTagManagerScript';
import { HorizonGoogleTagManagerNoScript } from '#/horizon/scripts/HorizonGoogleTagManagerNoScript';
import { trackError } from '#/utils/trackError';
import { log } from '#/utils/logging';
import { HorizonPolyfillScript } from '#/horizon/scripts/HorizonPolyfillScript';

export async function loader({
  request,
  context,
}: HorizonLoaderFunctionArgs<void>) {
  const { user, environment, appConfig } =
    await apiIsomorphicFetchUserEnvironment({
      request,
      context,
    })();

  return {
    user,
    environment,
    appConfig,
    contextLocale: context.locale,
    flash: context.flash,
    settings: {
      siteVerification: context.horizonServerEnv.googleSiteVerification,
      tagManager: context.horizonServerEnv.googleTagManager
        ? { trackingId: context.horizonServerEnv.googleTagManager.trackingId }
        : null,
      trackJs: context.horizonServerEnv.trackJs
        ? {
            token: context.horizonServerEnv.trackJs.token,
            application: context.horizonServerEnv.trackJs.application,
            version: context.horizonServerEnv.trackJs.version,
          }
        : null,
      mixpanel: context.horizonServerEnv.mixpanel
        ? {
            token: context.horizonServerEnv.mixpanel.token,
            debug: context.horizonServerEnv.mixpanel.debug,
          }
        : null,
      maps: context.horizonServerEnv.googleMaps
        ? { apiKey: context.horizonServerEnv.googleMaps.apiKey }
        : null,
    },
  };
}

export function shouldRevalidate() {
  return false;
}

export function links(): LinkDescriptor[] {
  return [
    {
      rel: 'preload',
      href: `/static/fonts/source-sans-3/source-sans-3-latin-wght-normal.woff2`,
      as: 'font',
      type: 'font/woff',
      crossOrigin: 'anonymous',
    },
  ];
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { user, environment, appConfig, contextLocale, settings } =
    useLoaderData<HorizonLoaderFunctionData<typeof loader>>();

  const { overrideLocale } = useMatchesOverrideLocale();
  const locale = overrideLocale ?? contextLocale;

  const i18n = createI18nFn(locale);
  const appName = i18n('Idealist');

  const { isInEmbedSearchWidget } = useIsInEmbedSearchWidget();

  return (
    <html
      lang={locale}
      // Auto-translate can crash the app with the error
      // Failed to execute 'removeChild' on 'Node': The node to be removed is not a child
      translate="no"
      className="notranslate"
    >
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, minimum-scale=1, shrink-to-fit=no"
        />
        <meta httpEquiv="x-ua-compatible" content="ie=edge" />

        <meta
          name="google"
          // Additional layer to ask browser and search engines
          // to no auto-translate the website because it can crash React
          content="notranslate"
        />

        {settings.siteVerification && (
          <meta
            name="google-site-verification"
            content={settings.siteVerification}
          />
        )}

        {/* Favicons & app meta */}
        <link rel="manifest" href="/manifest.json" />
        <meta name="apple-mobile-web-app-title" content={appName} />
        <meta name="application-name" content={appName} />
        <meta name="theme-color" content="#ffffff" />

        <link rel="shortcut icon" href="/favicon.ico" />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#0275d8" />

        {/* Styles & links */}
        <style>{themeGlobalCss}</style>
        <Links />

        {/* Polyfill & prepare environment */}
        <HorizonPolyfillScript />
        <HorizonSetIdealistObjectScript
          user={user}
          environment={environment}
          appConfig={appConfig}
        />
        <HorizonBrowserNotSupportedScript />

        {/* Tracking */}
        {settings.trackJs && (
          <HorizonTrackJsScript
            token={settings.trackJs.token}
            application={settings.trackJs.application}
            version={settings.trackJs.version}
          />
        )}

        {settings.mixpanel && (
          <HorizonMixpanelScript
            token={settings.mixpanel.token}
            debug={settings.mixpanel.debug}
          />
        )}

        {settings.tagManager && (
          <HorizonGoogleTagManagerScript
            trackingId={settings.tagManager.trackingId}
          />
        )}

        {/* Additional libs */}
        <HorizonGoogleMapsScripts apiKey={settings.maps?.apiKey || null} />
      </head>
      <body>
        {settings.tagManager && (
          <HorizonGoogleTagManagerNoScript
            trackingId={settings.tagManager.trackingId}
          />
        )}
        {children}

        {!isInEmbedSearchWidget && <ScrollRestoration />}
        <Scripts />
      </body>
    </html>
  );
}

export default function Root({
  loaderData: { user, environment, appConfig, contextLocale, flash },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { overrideLocale } = useMatchesOverrideLocale();
  const locale = overrideLocale ?? contextLocale;

  const localeContextValue = useMemo(
    () => ({ locale, i18n: createI18nFn(locale) }),
    [locale],
  );

  useEffect(() => {
    // Clear flash messages after showing it
    deleteCookie(FLASHES_COOKIE);
  }, []);

  return (
    <AppConfigContextProvider appConfig={appConfig}>
      <HorizonFlashStoreProvider initialState={{ flash }}>
        <I18nContext value={localeContextValue}>
          <UserEnvironmentContextProvider
            initialState={{
              user,
              environment,
              actionsWhileLoggedOutProcessedAt: null,
            }}
          >
            <App />
          </UserEnvironmentContextProvider>
        </I18nContext>
      </HorizonFlashStoreProvider>
    </AppConfigContextProvider>
  );
}

function Error500WithTrace({ error }: { error: Error }) {
  useEffect(() => {
    log.error(`stacktrace:\n${error.stack}`);
    trackError(error);
  }, [error]);

  return (
    <div style={{ padding: 40, maxWidth: 600, margin: '0 auto' }}>
      <h1>Something went wrong</h1>

      <p>
        We're sorry, something went wrong. Please try again and if the problem
        persists, please contact us via{' '}
        <a href="https://www.idealist.org/en/about/contact">this form</a> or at{' '}
        <a href="mailto:support@idealist.org">support@idealist.org</a>.
      </p>

      <footer>
        <a href="/">Go back to the site</a>
      </footer>
    </div>
  );
}

export function ErrorBoundary({ error }: { error: Error }) {
  if (import.meta.env.DEV && error && error instanceof Error) {
    return (
      <>
        <h1>Error</h1>
        <p>{error.message}</p>

        {error.stack && (
          <pre className="w-full p-4 overflow-x-auto">
            <code>{error.stack}</code>
          </pre>
        )}
      </>
    );
  }

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <h1>404</h1>
          <p>The requested page could not be found.</p>
        </div>
      );
    }
  }

  return <Error500WithTrace error={error} />;
}
